import React, { useEffect, useRef, useState } from "react"
import { connect, useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import { Button } from "components/Button"
import { Card } from "components/Card/Card"
import { UiCarousel } from "components/Carousel/Index"
import { UiFooter } from "components/Carousel/Ui"
import Loader from "components/Loaders/Loader/Loader"
import documentGenerationAnimation from "assets/animations/lottie_document_generation.json"
import { GenericModal, IneligibilityModal, Modal, UploadModal } from "components/Modal/Modal"
import { ContraceptionModal } from "components/Modal/ContraceptionModal"
import { RemoveFamilyDoctorModal } from "components/Modal/RemoveFamilyDoctorModal"
import { openNotification } from "components/Message/Message"
import PreviousButton from "components/Previous/PreviousButton"
import { Steps } from "components/Steps"
import { useStepper } from "components/Steps/hooks"
import { InsertCarerModal } from "containers/InsertCarer"
import { cdn } from "core/cdn"
import {
  CHECKUP_REPORT_PAGE,
  PAYMENT_PAGE,
  PHARMACIST_END_PAGE,
  PREVENTION_INTRO_PAGE,
  PROFILE_PAGE,
  TELEEXPERTISE_CONSENT_PAGE,
} from "core/constants"
import { Carer, DoctorType, Gender, TreatingDoctorStatus } from "types/entity"
import { Dispatcher } from "types/redux"
import { SurveyStore, UploadDocStore } from "types/store"


import { BP_PATH, PATH, preventionDocumentLoader, reasonValues, subTitles } from "./models"
import actions from "./services/actions"
import {
  ACTION,
  Answer,
  Question,
  QUESTION_TYPE,
  Reply,
  SurveyProps,
  Value,
} from "./types"
import { isValueSelected, valueEquals } from "./utils"
import { getCurrentSelectedPatientId, getDoctorTypeAnswer, getCurrentPatientFamilyDoctor, getCurrentPatientGender } from "./services/selector"
import { useLanguage } from "locales"
import { UiQuestionsLine } from "components/Question/Question"
import { Slider } from "components/Slider/Slider"
import { InfoList } from "components/InfoList/InfoList"

const DERMATO_REASONS = [reasonValues.RASH, reasonValues.STING, reasonValues.BURN]

const DefaultReply = (props: { reply: Reply; onSelect: () => void }) => {
  return (
    <Button onClick={props.onSelect} wide="long" type={props.reply?.ui?.type}>
      {props.reply.title}
    </Button>
  )
}

const Index: React.FC<SurveyProps> = (props): JSX.Element => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const { current: question, answer: selectedItems, answers } = props
  const [tooYoungModalVisible, setTooYoungModalVisible] = useState(false)
  const [wrongAgePreventionModal, setWrongAgePreventionModal] = useState<boolean>(false);
  const [insertCarerModalVisible, setInsertCarerModalVisible] = useState(false)
  const [contraceptionModalVisible, setContraceptionModalVisible] = useState(false)
  const [maxRepliesModalVisible, setMaxRepliesModalVisible] = useState(false)
  const [removeFamilyDoctorModalVisible, setRemoveFamilyDoctorModalVisible] = useState(false)
  const [removingFamilyDoctor, setRemovingFamilyDoctor] = useState(false)
  const [pendingContraceptionReply, setPendingContraceptionReply] =
    useState<Reply | null>(null)
  const [uploadModalDisplayCount, setUploadModalDisplayCount] = useState(0)
  const [showUploadRecommendedModal, setShowUploadRecommendedModal] =
    useState(false)
  const [TLCReason, setTLCReason] = useState("")
  const [uploadModalTitle, setUploadModalTitle] = useState("")
  const currentPatientId = useSelector(getCurrentSelectedPatientId)
  const currentPatientGender = useSelector(getCurrentPatientGender)
  const doctorType = useSelector(getDoctorTypeAnswer)
  const hasPhonePhotos = useSelector(
    ({ upload }: { upload: UploadDocStore }) => !!upload.capturedPhonePhotos?.length,
  )
  const currentPatientFamilyDoctor = useSelector(getCurrentPatientFamilyDoctor)
  // The API may return an empty object when no treating doctor is set
  const hasFamilyDoctor = Object.keys(currentPatientFamilyDoctor ?? {}).length > 0
  const isDoctorAvailableWithFamilyDoctor = question?.id === PATH.doctor_available && hasFamilyDoctor
  // Computed at render time: the stored question is not rebuilt when the
  // treating doctor is added or removed during the survey. The question is
  // undefined until the survey is initialized (a loader is rendered then).
  const displayedReplies = isDoctorAvailableWithFamilyDoctor
    ? question.replies.filter(({ value }) => value !== TreatingDoctorStatus.NO_DOCTOR)
    : question?.replies ?? []
  const restoredDocSelection = useSelector(
    ({ survey }: { survey: SurveyStore }) => survey.restoredDocSelection,
  )
  const navigate = useNavigate()
  const location = useLocation()
  const patientIdToSelect = (location.state as { selectedPatientId?: number } | null)
    ?.selectedPatientId
  const patientAutoSelected = useRef(false)

  // previous() skips redirect steps: back to the last intro slide explicitly
  const backToPreventionIntro = () => {
    dispatch(actions.jumpTo(BP_PATH.prevention_intro as any))
    navigate(PREVENTION_INTRO_PAGE, { replace: true, state: { fromEnd: true } })
  }

  useEffect(() => {
    if ([PATH.selected_documents, PATH.selected_documents_prevention].includes(question?.id as any)) {
      ;(props as any).resetReply(PATH.selected_documents)
      props.getUploadedDocuments()

      const interval = setInterval(() => {
        props.getUploadedDocuments()
      }, 5000)
      return () => clearInterval(interval)
    }

    if (question?.id === PATH.upload_documents) {
      setUploadModalDisplayCount(0)
      ;(props as any).resetReply(PATH.upload_documents)
      if (props.pathway !== DoctorType.DERMATOLOGY) {
        const reason = answers.find((a) => a.questionId === "reason")
          .value[0] as string
        setTLCReason(reason)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, dispatch])

  useEffect(() => {
    if (
      [PATH.selected_documents, PATH.selected_documents_prevention].includes(question?.id as any) &&
      question.replies.length > 0
    ) {
      // A selection restored from the persisted snapshot replaces the
      // recent-documents preselection, and is only consumed once
      const restoredValues = restoredDocSelection?.[question.id]
      question.replies.forEach((reply) => {
        const shouldSelect = restoredValues
          ? restoredValues.includes(reply.value)
          : reply.preselect
        if (shouldSelect) props.set([reply])
      })
      if (restoredValues) dispatch(actions.clearRestoredDocSelection())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, question.replies.length])

  useEffect(() => {
    if (props.current.type === QUESTION_TYPE.redirect)
      navigate(props.current.value, { replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.current])

  useEffect(() => {
    // Back from the relative creation: select it as soon as its reply exists
    if (question?.id !== PATH.patient || patientAutoSelected.current || !patientIdToSelect) return
    const reply = question.replies.find(({ value }) => value === patientIdToSelect)
    if (!reply) return
    patientAutoSelected.current = true
    onSelect(reply)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.replies, patientIdToSelect])

  useEffect(() => {
    switch (props.status) {
      case "finish":
        // Check Up pathway is free
        if (props.pathway === DoctorType.CHECKUP) {
          navigate(CHECKUP_REPORT_PAGE)
          return
        }
        if (props.pathway === DoctorType.PHARMACIST) {
          navigate(PHARMACIST_END_PAGE)
          return
        }
        if (props.pathway === DoctorType.DERMATOLOGY) {
          navigate(TELEEXPERTISE_CONSENT_PAGE)
          return
        }
        props.submit(props.answers)
        navigate(PAYMENT_PAGE)
        return
      case "cancel":
        props.reset()
        navigate(PROFILE_PAGE)
        return
      case "on_going":
        break
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.status])

  const onSelect = (r: Reply) => {
    if (
      props.pathway !== DoctorType.DERMATOLOGY &&
      question?.id === PATH.upload_documents &&
      !r.value &&
      uploadModalDisplayCount === 0 &&
      (
        [languages.Renewal, languages.lab_results].includes(TLCReason) ||
        DERMATO_REASONS.includes(TLCReason)
      )
    ) {
      const isRenewal = languages.Renewal === TLCReason
      const isDermato = DERMATO_REASONS.includes(TLCReason)
      setUploadModalTitle(
        isRenewal
          ? languages.UploadRecommendedText
          : isDermato
          ? languages.dermatoModalText
          : languages.photoTransferText,
      )
      setShowUploadRecommendedModal(true)
      setUploadModalDisplayCount((c) => c + 1)
      return
    }

    if(doctorType === DoctorType.PHARMACIST && question?.id === PATH.patient && r.preventionReportAllowed === false) {
      setWrongAgePreventionModal(true)
      return;
    }

    if (question?.id === BP_PATH.prevention_pharmacist_confirm && r.value === "pharmacist_cancelled") {
      props.reset()
      navigate(PROFILE_PAGE)
      return
    }

    if (
      question?.id === PATH.pharmacist_must_be_here &&
      r.value === "pathway_choice_cancelled"
    ) {
      props.reset()
      navigate(PROFILE_PAGE)
      return
    }

    if (r.action === ACTION.redirect) {
      navigate(r.value as string, { replace: true })
      return
    }
    if (r.action === ACTION.inability) {
      if (r.value === "relative_too_young") {
        setTooYoungModalVisible(true)
      }
      return
    }
    if (
      question?.multipleReplies &&
      question?.maxReplies &&
      selectedItems.length >= question.maxReplies
    ) {
      setMaxRepliesModalVisible(true)
      return
    }
    if (
      question?.id === PATH.upload_documents &&
      r.action === ACTION.subquestion
    ) {
      const oldValues = question.replies.filter(
        (old) => !valueEquals(old.value, r.value),
      )
      props.unset(oldValues)
      props.set([r])
      props.getUploadedDocuments("forward")
      return
    }
    if (r.value === reasonValues.CONTRACEPTION) {
      setPendingContraceptionReply(r)
      setContraceptionModalVisible(true)
      return
    }
    if (!question.multipleReplies) {
      const oldValues = question.replies.filter(
        (old) => !valueEquals(old.value, r.value),
      )
      props.unset(oldValues)
      props.set([r])
      // Le slider ne valide pas au clic : l'utilisateur ajuste la valeur
      // puis confirme avec le bouton SUIVANT du footer.
      if (question.ui?.view !== "slider") props.next()
      return
    }
    props.set([r])
  }

  const removeFamilyDoctor = () => {
    setRemovingFamilyDoctor(true)
    dispatch(
      actions.removeFamilyDoctor({
        onSuccess: () => {
          setRemovingFamilyDoctor(false)
          setRemoveFamilyDoctorModalVisible(false)
          // Same answer and next step as "I do not have a treating doctor"
          const noDoctorReply = question.replies.find(
            ({ value }) => value === TreatingDoctorStatus.NO_DOCTOR,
          )
          if (!noDoctorReply) return
          props.unset(question.replies.filter((reply) => reply !== noDoctorReply))
          props.set([noDoctorReply])
          props.next()
        },
        onError: () => {
          setRemovingFamilyDoctor(false)
          openNotification({ type: "error", text: languages.removeFamilyDoctorError })
        },
      }),
    )
  }

  const unSelect = (r: Reply) => {
    if (!question.multipleReplies) {
      const oldValue = question.replies.filter((old) =>
        valueEquals(old.value, r.value),
      )
      if (oldValue) {
        props.unset([r])
        props.set([r])
        props.next()
      }
    } else {
      props.unsetStepReplies({ replies: [r] })
    }
  }

  const handleUploadModalAccept = () => {
    setShowUploadRecommendedModal(false)
    onSelect(question.replies[0])
  }

  const handleUploadModalCancel = () => {
    setShowUploadRecommendedModal(false)
    onSelect(question.replies[1])
  }

  const {
    current: currentStep,
    insideStep,
    totalInsideSteps,
    totalSteps,
  } = useStepper()

  const disabledNextButton = () => {
    // The API attaches the questionnaire PDF itself: sharing other documents is optional
    if (question?.id === PATH.selected_documents_prevention) return false
    if (question?.ui?.view === "questions_line") {
      return (
        selectedItems.length !==
        (question as Question & { questions: Question[] }).questions.length
      )
    }
    return !selectedItems.length && question.replies.length > 0
  }

  const showNextButton = () => {
    return (
      (props.pathway === DoctorType.DERMATOLOGY &&
        question?.id === PATH.upload_documents &&
        hasPhonePhotos) ||
      !!question.multipleReplies ||
      question.skipable ||
      question.ui?.view === "slider"
    )
  }

  if (
    !question ||
    question.type === QUESTION_TYPE.loader ||
    props.documentNavigationLoading
  ) {
    if (question?.id === preventionDocumentLoader.id)
      return (
        <Card
          animation={documentGenerationAnimation}
          title={languages.preventionDocumentGenerationTitle}
          subtitle={languages.preventionDocumentGenerationText_html}
        />
      )
    return <Loader />
  }

  return (
    <>
      <Steps
        current={currentStep}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      {question.id !== PATH.selected_documents_prevention && (
        <PreviousButton onClick={question.id === BP_PATH.prevention_pharmacist_confirm ? backToPreventionIntro : props.previous} />
      )}
      <Card
        title={question.title}
        subtitle={question.ui?.view === "slider" ? undefined : question.subtitle}
        subtitleCustomStyle={question.subtitleCustomStyle}
        imgUrl={question.imgUrl}
        titleSize={doctorType === DoctorType.PHARMACIST ? 'medium' : undefined}
        titleCustomStyle={
          question.id === PATH.upload_documents
            ? { marginBottom: 20 }
            : question.titleCustomStyle
        }
        order={question.order}
        doNotAnimateTitle={question.doNotAnimateTitle}
      >
        {question.ui?.view === "slider" && question.replies.length ? (
          // Slider hors UiCarousel : la question (subtitle) est encadrée
          // avec le rail, sous le titre principal de la Card.
          <Slider
            items={question.replies}
            label={question.subtitle}
            selectedIndex={question.replies.findIndex((reply) =>
              isValueSelected(selectedItems, reply.value),
            )}
            onSelect={(item) => onSelect(item as Reply)}
          />
        ) : question.replies.length ? (
          <UiCarousel
            variant={doctorType === DoctorType.PHARMACIST && /prevention-report/.test(question.id) ? 'small' : undefined}
            items={displayedReplies}
            selected={selectedItems}
            onSelect={onSelect}
            unSelect={unSelect}
            ui={question.ui}
          />
        ) : question.ui?.view === "questions_line" ? (
          <UiQuestionsLine
            onSelect={onSelect}
            unSelect={unSelect}
            answers={selectedItems}
            question={question as any}
          />
        ) : null}

        {question.infoItems && <InfoList items={question.infoItems} />}

        {question.informationText && (
          <div className="information-card">{question.informationText}</div>
        )}

        <UiFooter
          style={question.id === PATH.inform && { paddingTop: 0 }}
          allowSkip={question.allowSkip}
          nextVisible={showNextButton()}
          nextDisabled={disabledNextButton()}
          onFinish={props.next}
          skipable={question.skipable}
          childrenFirst={[PATH.selected_documents, PATH.selected_documents_prevention].includes(question?.id as any)}
        >
          {question.defaultReply && (
            <DefaultReply
              reply={question.defaultReply}
              onSelect={() => {
                // default reply pass on the next direct
                onSelect(question.defaultReply as Reply)
              }}
            />
          )}
        </UiFooter>
        {isDoctorAvailableWithFamilyDoctor && (
          <div>
            <span
              className="small-link"
              onClick={() => setRemoveFamilyDoctorModalVisible(true)}
            >
              {currentPatientId
                ? languages.noLongerHaveDoctor_relative(currentPatientGender ?? Gender.MALE)
                : languages.noLongerHaveDoctor}
            </span>
          </div>
        )}
        <RemoveFamilyDoctorModal
          visible={removeFamilyDoctorModalVisible}
          loading={removingFamilyDoctor}
          text={
            currentPatientId
              ? languages.removeFamilyDoctorText_relative(currentPatientGender ?? Gender.MALE)
              : languages.removeFamilyDoctorText
          }
          onAccept={removeFamilyDoctor}
          onCancel={() => setRemoveFamilyDoctorModalVisible(false)}
        />
        {question.id === PATH.inform && (
          <div>
            <span
              className="small-link"
              onClick={() => setInsertCarerModalVisible(true)}
            >
              {currentPatientId
                ? languages.ImCaredForThisTlc_relative(currentPatientGender ?? Gender.MALE)
                : languages.ImCaredForThisTlc(currentPatientGender ?? Gender.MALE)}
            </span>
            <InsertCarerModal
              visible={insertCarerModalVisible}
              onFinish={(carer: Carer) => {
                setInsertCarerModalVisible(false)
                props.next()
              }}
              onCancel={() => setInsertCarerModalVisible(false)}
            />
          </div>
        )}

        <GenericModal
          visible={tooYoungModalVisible}
          title={languages.notSuitableTLC}
          acceptText={languages.IGotIt}
          onAccept={() => {
            setTooYoungModalVisible(false)
          }}
          contentText={languages.notSuitableTLCContent}
        />
        <GenericModal
          visible={maxRepliesModalVisible}
          acceptText={languages.IGotIt}
          acceptButtonProps={{ danger: false, type: "primary" }}
          onAccept={() => setMaxRepliesModalVisible(false)}
          style={{ width: "50vw", gap: "100px" }}
        >
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '45px', padding: "35px 35px 5px"}}>
            <div><img src={cdn("/images/warning_picto.svg")} width={'100px'} alt="Attention"/></div>
            <span style={{ fontWeight: 600, fontSize: "40px", textAlign: "center" }}>
              {languages.maxThreeReasonsTitle}
            </span>
          </div>
        </GenericModal>
        <ContraceptionModal
          open={contraceptionModalVisible}
          onAccept={() => {
            if (pendingContraceptionReply) {
              const oldValues = question.replies.filter(
                (old) => old.value !== pendingContraceptionReply.value,
              )
              props.unset(oldValues)
              props.set([pendingContraceptionReply])
              props.next()
            }
            setPendingContraceptionReply(null)
            setContraceptionModalVisible(false)
          }}
        />
        <UploadModal
          title={uploadModalTitle}
          onAccept={handleUploadModalAccept}
          onCancel={handleUploadModalCancel}
          visible={showUploadRecommendedModal}
        />
        <IneligibilityModal title={languages.youCantDoPrevention} visible={wrongAgePreventionModal} onCancel={() => navigate(PROFILE_PAGE)} onClose={() => setWrongAgePreventionModal(false)}/>
      </Card>
    </>
  )
}

const mapStateToProps = ({ survey }: { survey: SurveyStore }): SurveyProps => {
  const current = survey.current
  const doctorType = survey.answers.find(
    (a) => a.questionId === PATH.doctor_type,
  )?.value?.[0] as DoctorType | null

  if (
    doctorType !== DoctorType.DERMATOLOGY &&
    current.id === PATH.upload_documents
  ) {
    const reason = survey.answers.find((a) => a.questionId === "reason")
      .value[0]
    const subtitle = subTitles.filter((elem) => elem.reason === reason)[0]
      .subtitle
    current.subtitle = subtitle
  }
  const answer: Value[] =
    survey.answers.find(({ questionId }: Answer) => questionId === current?.id)
      ?.value || []
  return {
    current,
    answer,
    status: survey.status,
    answers: survey.answers,
    questions: survey.questions,
    path: survey.path,
    pathway: doctorType,
    documentNavigationLoading: survey.documentNavigationLoading,
  } as SurveyProps
}

const mapDispatchToProps = (dispatch: Dispatcher): Partial<SurveyProps> => {
  return {
    set: (replies_to_add: Reply[]) => dispatch(actions.set(replies_to_add)),
    unset: (replies_to_remove?: Reply[]) =>
      dispatch(actions.unset(replies_to_remove)),
    unsetStepReplies: (payload: { replies?: Reply[]; id?: PATH }) =>
      dispatch(actions.unsetStepReplies(payload)),
    next: () => dispatch(actions.next()),
    previous: () => dispatch(actions.previous()),
    reset: () => dispatch(actions.reset()),
    resetReply: (id: string) => dispatch(actions.resetReply(id)),
    resetReplies: () => dispatch(actions.resetReplies()),
    cancel: () => dispatch(actions.cancel()),
    submit: (a: Answer[]) => dispatch(actions.submit(a)),
    getUploadedDocuments: (navigation) =>
      dispatch(actions.getDocuments(navigation)),
  } as Partial<SurveyProps>
}

export default connect(mapStateToProps, mapDispatchToProps)(Index)
